import type { CharacterLook } from '../game/types';
import type { FighterPose, LegStyle, ShoeStyle, SideLook } from '../fight/types';
import { mixColor, noise, px, spriteFromDraw, type Sprite } from './sprite';

/*
 * Side-view pixel fighters for the shift-change brawls, built from the same
 * CharacterLook traits as the diner customers. Drawn facing right; the stage
 * flips them. Feet sit on the bottom row, horizontally centred at ANCHOR_X.
 */

export const SIDE_W = 38;
export const SIDE_H = 50;
export const ANCHOR_X = 16;

type Pt = readonly [number, number];

interface Rig {
  /** Whole-body offset (lean / bob). */
  dx: number;
  dy: number;
  /** Extra head offset on top of the body. */
  hx: number;
  hy: number;
  front: { elbow: Pt; fist: Pt };
  back: { elbow: Pt; fist: Pt };
  legs: 'stand' | 'stride' | 'together' | 'wide' | 'sit';
  face: 'normal' | 'angry' | 'hurt' | 'happy' | 'yell';
}

const shade = (c: string, k: number): string => mixColor(c, '#000000', k);
const light = (c: string, k: number): string => mixColor(c, '#ffffff', k);

const PANTS: Record<LegStyle, string> = {
  jeans: '#2f4f8f',
  jorts: '#5b86c4',
  skirt: '#fd79a8',
  slacks: '#2b2b33',
  sweats: '#555a66',
  shorts: '#c0392b',
};

function rigFor(pose: FighterPose, frame: number): Rig {
  const guard = {
    front: { elbow: [21, 28] as Pt, fist: [24, 24] as Pt },
    back: { elbow: [15, 28] as Pt, fist: [19, 25] as Pt },
  };
  switch (pose) {
    case 'walk':
      return {
        dx: 0,
        dy: frame % 2,
        hx: 0,
        hy: 0,
        ...guard,
        legs: frame % 2 ? 'together' : 'stride',
        face: 'normal',
      };
    case 'windup':
      return {
        dx: -1,
        dy: 0,
        hx: -1,
        hy: 0,
        front: { elbow: [16, 27], fist: [12, 24] },
        back: guard.back,
        legs: 'wide',
        face: 'angry',
      };
    case 'jab':
      return {
        dx: 1,
        dy: 0,
        hx: 1,
        hy: 0,
        front: { elbow: [25, 23], fist: [31, 23] },
        back: guard.back,
        legs: 'wide',
        face: 'angry',
      };
    case 'smack':
      return {
        dx: 2,
        dy: 1,
        hx: 2,
        hy: 1,
        front: { elbow: [25, 20], fist: [32, 27] },
        back: { elbow: [13, 27], fist: [11, 24] },
        legs: 'wide',
        face: 'yell',
      };
    case 'throw':
      return {
        dx: 1,
        dy: 0,
        hx: 1,
        hy: 0,
        front: { elbow: [23, 17], fist: [29, 13] },
        back: guard.back,
        legs: 'wide',
        face: 'yell',
      };
    case 'block':
      return {
        dx: -1,
        dy: 1,
        hx: -1,
        hy: 1,
        front: { elbow: [22, 26], fist: [23, 15] },
        back: { elbow: [20, 27], fist: [21, 16] },
        legs: 'wide',
        face: 'normal',
      };
    case 'hit':
      return {
        dx: -2,
        dy: 1,
        hx: -3,
        hy: 1,
        front: { elbow: [20, 20], fist: [23, 14] },
        back: { elbow: [11, 23], fist: [8, 19] },
        legs: 'stand',
        face: 'hurt',
      };
    case 'charge':
      return {
        dx: 3,
        dy: 2,
        hx: 3,
        hy: 2,
        front: { elbow: [26, 28], fist: [32, 29] },
        back: { elbow: [12, 29], fist: [9, 31] },
        legs: 'stride',
        face: 'yell',
      };
    case 'taunt':
      return {
        dx: 0,
        dy: 0,
        hx: 0,
        hy: 0,
        front: { elbow: [22, 18], fist: [25, 11] },
        back: { elbow: [13, 29], fist: [16, 32] },
        legs: 'stand',
        face: 'yell',
      };
    case 'victory':
      return {
        dx: 0,
        dy: frame % 2 ? -1 : 0,
        hx: 0,
        hy: 0,
        front: { elbow: [21, 16], fist: [22, 7] },
        back: { elbow: [15, 16], fist: [14, 7] },
        legs: 'wide',
        face: 'happy',
      };
    case 'super':
      return {
        dx: 2,
        dy: 0,
        hx: 2,
        hy: 0,
        front: { elbow: [25, 22], fist: [32, 21] },
        back: { elbow: [24, 26], fist: [31, 26] },
        legs: 'wide',
        face: 'yell',
      };
    case 'sit':
      return {
        dx: 0,
        dy: 11,
        hx: 0,
        hy: 0,
        front: { elbow: [21, 34], fist: [25, 36] },
        back: { elbow: [15, 35], fist: [19, 37] },
        legs: 'sit',
        face: frame % 2 ? 'happy' : 'normal',
      };
    case 'idle':
    default:
      return { dx: 0, dy: frame % 2, hx: 0, hy: 0, ...guard, legs: 'stand', face: 'normal' };
  }
}

function limb(ctx: CanvasRenderingContext2D, a: Pt, b: Pt, w: number, color: string): void {
  const steps = Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]), 1);
  for (let i = 0; i <= steps; i++) {
    const x = Math.round(a[0] + ((b[0] - a[0]) * i) / steps);
    const y = Math.round(a[1] + ((b[1] - a[1]) * i) / steps);
    px(ctx, x - Math.floor(w / 2), y - Math.floor(w / 2), w, w, color);
  }
}

function lookKey(s: SideLook): string {
  const l = s.look;
  return [
    s.cook ? 'cook' : 'npc',
    s.legs,
    s.shoes,
    l.skin,
    l.hair,
    l.hairStyle,
    l.hat,
    l.hatColor,
    l.eyes,
    l.facialHair,
    l.shirt,
    l.shirtAlt,
    l.shirtStyle,
    l.accessory,
  ].join('|');
}

export function sideSprite(
  side: SideLook,
  pose: FighterPose,
  frame: number,
  redFace = false,
): Sprite {
  return spriteFromDraw(
    `side:${lookKey(side)}:${pose}:${frame % 2}:${redFace}`,
    SIDE_W,
    SIDE_H,
    (ctx) => drawFighter(ctx, side, rigFor(pose, frame), redFace),
  );
}

function drawFighter(
  ctx: CanvasRenderingContext2D,
  side: SideLook,
  rig: Rig,
  redFace: boolean,
): void {
  const l = side.look;
  const skin = redFace ? mixColor(l.skin, '#e0413a', 0.45) : l.skin;
  const shirt = l.shirt;
  const sleeve = l.shirtStyle === 'tank' ? skin : shirt;
  const backSleeve = shade(sleeve, 0.22);
  const ox = rig.dx;
  const oy = rig.dy;
  const torsoX = 12 + ox;
  const torsoY = 21 + oy;
  const shoulderF: Pt = [18 + ox, 22 + oy];
  const shoulderB: Pt = [14 + ox, 22 + oy];
  const off = (p: Pt): Pt => [p[0] + ox, p[1] + oy];

  // Back arm (behind the body)
  limb(ctx, shoulderB, off(rig.back.elbow), 3, backSleeve);
  limb(
    ctx,
    off(rig.back.elbow),
    off(rig.back.fist),
    2,
    l.shirtStyle === 'tank' || l.shirtStyle === 'polo' ? shade(skin, 0.18) : backSleeve,
  );
  px(ctx, off(rig.back.fist)[0] - 1, off(rig.back.fist)[1] - 1, 3, 3, shade(skin, 0.15));

  // Long hair falls behind the shoulders
  if (l.hairStyle === 'long' || l.hairStyle === 'mullet') {
    px(
      ctx,
      12 + rig.hx + ox,
      12 + rig.hy + oy,
      4,
      l.hairStyle === 'long' ? 14 : 11,
      shade(l.hair, 0.12),
    );
  }

  drawLegs(ctx, side, rig);

  // Torso
  px(ctx, torsoX, torsoY + 1, 9, 13, shirt);
  px(ctx, torsoX + 1, torsoY, 7, 1, shirt);
  px(ctx, torsoX, torsoY + 1, 2, 13, shade(shirt, 0.18));
  drawShirtDetail(ctx, l, torsoX, torsoY, skin);
  if (side.cook) {
    // Apron over the front, strings at the back.
    px(ctx, torsoX + 4, torsoY + 5, 5, 11, '#3a3a44');
    px(ctx, torsoX + 4, torsoY + 5, 5, 1, '#55555f');
    px(ctx, torsoX, torsoY + 8, 4, 1, '#3a3a44');
  }
  // Neck
  px(ctx, 15 + ox + rig.hx, 19 + oy + rig.hy, 3, 3, shade(skin, 0.1));

  drawHead(ctx, l, 12 + ox + rig.hx, 9 + oy + rig.hy, rig.face, skin, side.cook === true);

  // Front arm
  limb(ctx, shoulderF, off(rig.front.elbow), 3, sleeve);
  limb(
    ctx,
    off(rig.front.elbow),
    off(rig.front.fist),
    2,
    l.shirtStyle === 'tank' || l.shirtStyle === 'polo' ? skin : sleeve,
  );
  const [fx, fy] = off(rig.front.fist);
  px(ctx, fx - 1, fy - 1, 3, 3, skin);
  if (side.cook) drawSpatula(ctx, off(rig.front.elbow), [fx, fy]);
}

function drawLegs(ctx: CanvasRenderingContext2D, side: SideLook, rig: Rig): void {
  const l = side.look;
  const pants = side.legs === 'skirt' ? l.shirt : PANTS[side.legs];
  const shortLegs = side.legs === 'jorts' || side.legs === 'shorts' || side.legs === 'skirt';
  const hipX = 12 + rig.dx;
  const hipY = 34 + rig.dy;

  const leg = (x: number, footX: number, color: string): void => {
    const top = hipY;
    const bottom = 47;
    if (shortLegs) {
      const knee = top + 6;
      limb(ctx, [x, top], [Math.round((x + footX) / 2), knee], 3, color);
      limb(ctx, [Math.round((x + footX) / 2), knee], [footX, bottom - 1], 2, l.skin);
    } else {
      limb(ctx, [x, top], [footX, bottom - 1], 3, color);
    }
    drawShoe(ctx, side.shoes, footX, bottom, l.skin);
  };

  if (rig.legs === 'sit') {
    // Sitting on the diner floor, legs out in front.
    px(ctx, hipX, 44, 10, 3, pants);
    px(ctx, hipX + 9, 45, 8, 2, shortLegs ? l.skin : pants);
    drawShoe(ctx, side.shoes, hipX + 17, 47, l.skin);
    return;
  }
  const spread =
    rig.legs === 'stride' ? 4 : rig.legs === 'wide' ? 3 : rig.legs === 'together' ? 1 : 2;
  leg(hipX + 2, hipX + 2 - spread, shade(pants, 0.2));
  leg(hipX + 6, hipX + 6 + spread, pants);
  if (side.legs === 'skirt') {
    px(ctx, hipX - 1, hipY - 1, 12, 5, l.shirt);
    px(ctx, hipX - 2, hipY + 3, 14, 2, shade(l.shirt, 0.15));
  } else {
    px(ctx, hipX, hipY - 1, 10, 2, shade(pants, 0.1));
    if (side.legs === 'jorts')
      for (let i = 0; i < 4; i++) px(ctx, hipX + i * 3, hipY + 6, 1, 1, '#dfe8f5');
  }
}

function drawShoe(
  ctx: CanvasRenderingContext2D,
  shoes: ShoeStyle,
  x: number,
  y: number,
  skin: string,
): void {
  switch (shoes) {
    case 'barefoot':
      px(ctx, x - 1, y - 1, 5, 2, skin);
      px(ctx, x + 3, y - 1, 1, 1, shade(skin, 0.2));
      break;
    case 'heels':
      px(ctx, x - 1, y - 2, 4, 2, '#e84393');
      px(ctx, x - 1, y, 1, 1, '#e84393');
      break;
    case 'slides':
      px(ctx, x - 1, y - 1, 5, 1, skin);
      px(ctx, x - 1, y, 5, 1, '#2d3436');
      break;
    case 'boots':
      px(ctx, x - 1, y - 3, 5, 4, '#6b4125');
      break;
    default:
      px(ctx, x - 1, y - 2, 5, 2, '#f5f5f5');
      px(ctx, x - 1, y, 5, 1, '#9aa3ad');
  }
}

function drawShirtDetail(
  ctx: CanvasRenderingContext2D,
  l: CharacterLook,
  x: number,
  y: number,
  skin: string,
): void {
  switch (l.shirtStyle) {
    case 'tank':
      px(ctx, x + 3, y, 5, 2, skin);
      break;
    case 'flannel':
      for (let yy = y + 1; yy < y + 14; yy++)
        for (let xx = x; xx < x + 9; xx++)
          if ((xx - x) % 3 === 0 || (yy - y) % 3 === 0)
            px(ctx, xx, yy, 1, 1, shade(l.shirtAlt, 0.3));
      break;
    case 'hawaiian':
      for (let i = 0; i < 8; i++) {
        px(
          ctx,
          x + 1 + Math.floor(noise(i, 2, 5) * 7),
          y + 2 + Math.floor(noise(i, 3, 5) * 11),
          1,
          1,
          i % 2 ? '#fff4c2' : '#ff7eb6',
        );
      }
      break;
    case 'sparkle':
      for (let i = 0; i < 7; i++) {
        px(
          ctx,
          x + Math.floor(noise(i, 7, 3) * 9),
          y + 1 + Math.floor(noise(i, 8, 3) * 12),
          1,
          1,
          i % 2 ? '#ffffff' : '#ffe680',
        );
      }
      break;
    case 'polo':
      px(ctx, x + 5, y, 4, 2, l.shirtAlt);
      px(ctx, x + 7, y + 2, 1, 3, shade(l.shirt, 0.2));
      break;
    case 'stripes':
      for (let yy = y + 2; yy < y + 14; yy += 3) px(ctx, x, yy, 9, 1, l.shirtAlt);
      break;
    default:
      px(ctx, x + 6, y, 3, 2, skin);
  }
  switch (l.accessory) {
    case 'sash':
      for (let i = 0; i < 12; i++)
        px(ctx, x + Math.min(8, Math.floor(i * 0.75)), y + i + 1, 2, 1, '#ffffff');
      break;
    case 'chain':
      for (let i = 0; i < 5; i++)
        px(ctx, x + 4 + i, y + 2 + Math.round(Math.sin((i / 4) * Math.PI) * 2), 1, 1, '#f5c542');
      break;
    case 'bowtie':
      px(ctx, x + 7, y, 2, 2, '#111');
      break;
    default:
      break;
  }
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  l: CharacterLook,
  hx: number,
  hy: number,
  face: Rig['face'],
  skin: string,
  cook: boolean,
): void {
  const rows = [
    '..######..',
    '.########.',
    '##########',
    '##########',
    '###########',
    '##########',
    '#########.',
    '#########.',
    '.#######..',
    '..#####...',
  ];
  const skinShade = shade(skin, 0.14);
  // Hair that sits behind the head
  if (l.hairStyle === 'afro') {
    for (let y = -4; y <= 7; y++)
      for (let x = -3; x <= 9; x++) {
        const dx = x - 3;
        const dy = y - 1.5;
        if (dx * dx + dy * dy * 1.3 < 38)
          px(ctx, hx + x, hy + y, 1, 1, noise(x, y, 11) > 0.8 ? light(l.hair, 0.15) : l.hair);
      }
  }
  if (l.hairStyle === 'bun') px(ctx, hx - 2, hy - 1, 4, 4, l.hair);
  if (l.hairStyle === 'ponytail') px(ctx, hx - 2, hy + 2, 3, 7, l.hair);

  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== '#') continue;
      px(ctx, hx + x, hy + y, 1, 1, x <= 2 && y >= 2 ? skinShade : skin);
    }
  });
  // Ear
  px(ctx, hx + 3, hy + 4, 2, 2, skinShade);

  // Eyes & brows
  const eye = '#1a1410';
  if (l.eyes === 'sunglasses') {
    px(ctx, hx + 5, hy + 3, 5, 2, '#0d0d12');
    px(ctx, hx + 8, hy + 3, 1, 1, '#6d8bd1');
  } else if (face === 'happy') {
    px(ctx, hx + 7, hy + 3, 2, 1, eye);
  } else if (face === 'hurt') {
    px(ctx, hx + 6, hy + 3, 1, 1, eye);
    px(ctx, hx + 8, hy + 3, 1, 1, eye);
    px(ctx, hx + 7, hy + 4, 1, 1, eye);
  } else {
    px(ctx, hx + 7, hy + 3, 1, 2, eye);
    if (l.eyes === 'glasses') {
      px(ctx, hx + 6, hy + 2, 4, 1, '#2d2d33');
      px(ctx, hx + 6, hy + 5, 4, 1, '#2d2d33');
      px(ctx, hx + 4, hy + 3, 2, 1, '#2d2d33');
    }
  }
  if (face === 'angry' || face === 'yell') {
    px(ctx, hx + 6, hy + 1, 2, 1, eye);
    px(ctx, hx + 8, hy + 2, 1, 1, eye);
  }
  // Mouth
  if (face === 'yell' || face === 'hurt') {
    px(ctx, hx + 8, hy + 6, 2, 2, '#3a1512');
  } else if (face === 'happy') {
    px(ctx, hx + 7, hy + 6, 1, 1, shade(skin, 0.45));
    px(ctx, hx + 8, hy + 7, 2, 1, shade(skin, 0.45));
  } else {
    px(ctx, hx + 8, hy + 7, 2, 1, shade(skin, 0.45));
  }
  // Facial hair
  const fh = shade(l.hair, 0.05);
  if (l.facialHair === 'beard') {
    px(ctx, hx + 4, hy + 6, 5, 3, fh);
    px(ctx, hx + 5, hy + 9, 4, 1, fh);
  } else if (l.facialHair === 'mustache') {
    px(ctx, hx + 7, hy + 5, 3, 1, fh);
  } else if (l.facialHair === 'stubble') {
    for (let i = 0; i < 6; i++)
      px(ctx, hx + 4 + (i % 3) * 2, hy + 6 + Math.floor(i / 3) * 2, 1, 1, shade(skin, 0.3));
  }

  // Hair on top / at the back
  const hair = l.hair;
  const hi = light(hair, 0.18);
  switch (l.hairStyle) {
    case 'bald':
      px(ctx, hx + 4, hy, 2, 1, light(skin, 0.35));
      break;
    case 'buzz':
      px(ctx, hx + 2, hy, 6, 1, shade(hair, 0.1));
      px(ctx, hx + 1, hy + 1, 4, 1, shade(hair, 0.1));
      break;
    case 'mohawk':
      px(ctx, hx + 3, hy - 3, 3, 4, hair);
      break;
    case 'afro':
      break;
    case 'curly':
      for (let x = 0; x < 9; x++) px(ctx, hx + x, hy - 1 + (x % 2), 1, 1, hair);
      px(ctx, hx, hy, 8, 2, hair);
      px(ctx, hx, hy + 2, 3, 4, hair);
      break;
    default:
      px(ctx, hx + 1, hy - 1, 7, 1, hair);
      px(ctx, hx, hy, 9, 2, hair);
      px(ctx, hx, hy + 2, 3, l.hairStyle === 'long' ? 8 : 4, hair);
      px(ctx, hx + 2, hy, 3, 1, hi);
  }

  // Hats (side profile, brims point forward)
  const c = l.hatColor;
  switch (l.hat) {
    case 'trucker':
    case 'cap':
      px(ctx, hx, hy - 2, 9, 4, c);
      if (l.hat === 'trucker') px(ctx, hx, hy - 2, 4, 3, '#f2efe6');
      px(ctx, hx + 8, hy + 1, 5, 1, shade(c, 0.25));
      break;
    case 'visor':
      px(ctx, hx, hy + 1, 9, 1, c);
      px(ctx, hx + 8, hy + 1, 5, 2, c);
      px(ctx, hx + 8, hy + 2, 5, 1, shade(c, 0.3));
      break;
    case 'headband':
      px(ctx, hx, hy + 1, 10, 2, c);
      px(ctx, hx - 2, hy + 2, 2, 3, c);
      break;
    case 'tiara':
      px(ctx, hx + 2, hy - 1, 5, 1, '#ffd23f');
      px(ctx, hx + 4, hy - 3, 1, 2, '#ffd23f');
      px(ctx, hx + 4, hy - 3, 1, 1, '#ff5fa2');
      break;
    case 'beanie':
      px(ctx, hx, hy - 3, 9, 5, c);
      px(ctx, hx, hy + 1, 9, 1, shade(c, 0.25));
      break;
    case 'cowboy':
      px(ctx, hx + 1, hy - 4, 7, 4, c);
      px(ctx, hx - 3, hy, 16, 1, c);
      break;
    default:
      break;
  }
  if (cook && l.hat === 'none') px(ctx, hx, hy - 2, 9, 3, '#f5f5f5');
}

function drawSpatula(ctx: CanvasRenderingContext2D, elbow: Pt, fist: Pt): void {
  // The handle continues the forearm's direction; the blade sits on the end.
  const dx = fist[0] - elbow[0];
  const dy = fist[1] - elbow[1];
  const len = Math.max(1, Math.hypot(dx, dy));
  const ux = dx / len;
  const uy = dy / len;
  const tip: Pt = [Math.round(fist[0] + ux * 5), Math.round(fist[1] + uy * 5)];
  limb(ctx, fist, tip, 1, '#4a2f1d');
  px(ctx, tip[0] - 1 + Math.round(ux), tip[1] - 2 + Math.round(uy), 4, 4, '#c7ccd3');
  px(ctx, tip[0] - 1 + Math.round(ux), tip[1] - 2 + Math.round(uy), 4, 1, '#eef1f4');
}
